import React from 'react';
import { useParams } from 'react-router';

import { NEW_VIEW } from '@/constants/dashboardConstants';
import useProject from '@API/project/useProject';
import { DashboardView } from '@experiment-management-shared/types';
import ConfirmRegenerateModal from '@shared/components/ViewSelect/modals/ConfirmRegenerateModal';
import DeleteViewModal from '@shared/components/ViewSelect/modals/DeleteViewModal';
import FormViewModal from '@shared/components/ViewSelect/modals/FormViewModal';
import ViewSelect from '@shared/components/ViewSelect/ViewSelect';
import { useIsUserLoggedIn } from '@shared/hooks';
import useDashboardViewActions from './hooks/useDashboardViewActions';
import useDashboardViewDelete from './hooks/useDashboardViewDelete';
import useDashboardViewOptions from './hooks/useDashboardViewOptions';
import useDefaultViewOption from './hooks/useDefaultViewOption';

const DashboardViewSelect = ({
  dashboard,
  resetDashboardView
}: {
  dashboard: DashboardView;
  resetDashboardView: VoidFunction;
}) => {
  const { viewId } = useParams<{ viewId?: string }>();
  const { data: project } = useProject();
  const { projectViewList, workspaceViewList } = useDashboardViewOptions();
  const isUserLoggedIn = useIsUserLoggedIn();
  const { onDeleteTemplate, deleteModalConfig } = useDashboardViewDelete();
  const {
    onSetAsDefaultView,
    onSelectView,
    onEditTemplate,
    onDuplicateTemplate,
    onCreateNewTemplate,
    actionModalConfig
  } = useDashboardViewActions({ dashboard });
  const {
    confirmRegenerateModalConfig,
    defaultView,
    onRegenerateDefaultView
  } = useDefaultViewOption({
    isCurrentViewFrozen: dashboard.isFrozenView ?? false,
    resetDashboardView
  });

  const canEdit = project?.canEdit;
  const selectedViewId = viewId === NEW_VIEW ? NEW_VIEW : dashboard.templateId;

  const optionList = [
    {
      title: 'Views By Comet',
      itemList: [defaultView]
    },
    {
      title: 'Project views',
      itemList: projectViewList
    },
    {
      title: 'workspace views',
      infoText:
        'Browse views from all projects within this workspace. Any changes will be saved to a duplicated view within this project.',
      isExpanded: workspaceViewList.some(
        option => option.isDefault || option.id === selectedViewId
      ),
      itemList: workspaceViewList
    }
  ];

  const handleSetDefault = (viewName: string, viewId?: string) => {
    onSetAsDefaultView({
      viewId,
      viewName,
      showMessage: true
    });
  };

  return (
    <>
      <ViewSelect
        optionList={optionList}
        selectedViewId={selectedViewId}
        onSelectView={onSelectView}
        onCreateNewView={onCreateNewTemplate}
        onEditView={onEditTemplate}
        onDuplicateView={onDuplicateTemplate}
        onSetDefaultView={handleSetDefault}
        onDeleteView={onDeleteTemplate}
        onRegenerateDefaultView={onRegenerateDefaultView}
        indicatorLabel="Syncing is paused and new panels will not be shown. Regenerating the view updates panels and discards manual edits. You can save your changes by duplicating the view."
        actionsDisabled={!canEdit}
        disabled={!isUserLoggedIn}
      />

      <ConfirmRegenerateModal {...confirmRegenerateModalConfig} />
      <DeleteViewModal {...deleteModalConfig} />
      <FormViewModal {...actionModalConfig} />
    </>
  );
};

export default DashboardViewSelect;
